import { ActionIcon, Checkbox, Group, Text, UnstyledButton } from '@mantine/core';
import { IconChevronDown, IconSettings } from '@tabler/icons-react';
import type { ColumnDef } from '@tanstack/react-table';
import { i18nKeys } from 'locales';
import type { Id } from 'types';
import type { TableColumn } from 'types/views';
import { EDIT_COLUMNS_COLUMN_ID, SELECTION_COLUMN_ID } from './columnIds';
import {
  type CellContext as RegistryCellContext,
  resolveCellContent,
} from './TableItem/cellRenderers';
import { RowLeadingCell } from './TableItem/RowLeadingCell';

type ViewRow = Record<string, unknown> & { id: Id };

export type ViewTableMeta = RegistryCellContext & {
  sortBy?: string;
  sortOrder?: string;
  onSort: (columnName: string) => void;
  selectedIds: readonly Id[];
  toggleRow: (id: Id) => void;
  areAllItemsOnThisPageSelected: boolean;
  onToggleSelectAllOnPage: () => void;
  onEditColumns: () => void;
};

type BuildViewColumnDefsOptions = {
  columns: TableColumn[];
  isSelectable?: boolean;
};

const metaOf = (table: { options: { meta?: unknown } }): ViewTableMeta =>
  table.options.meta as ViewTableMeta;

const selectionColumn: ColumnDef<ViewRow> = {
  id: SELECTION_COLUMN_ID,
  header: ({ table }) => {
    const { t, areAllItemsOnThisPageSelected, onToggleSelectAllOnPage } = metaOf(table);
    return (
      <Checkbox
        size="sm"
        checked={areAllItemsOnThisPageSelected}
        onChange={onToggleSelectAllOnPage}
        aria-label={t(i18nKeys.FORM.SELECT_ALL)}
      />
    );
  },
  cell: ({ row, table }) => {
    const { selectedIds, toggleRow } = metaOf(table);
    const item = row.original;
    return (
      <RowLeadingCell
        item={item}
        isSelected={selectedIds.includes(item.id)}
        onToggleSelect={() => toggleRow(item.id)}
      />
    );
  },
};

export const buildViewColumnDefs = ({
  columns,
  isSelectable = true,
}: BuildViewColumnDefsOptions): ColumnDef<ViewRow>[] => [
  ...(isSelectable ? [selectionColumn] : []),
  ...columns.map(
    (column): ColumnDef<ViewRow> => ({
      id: column.name,
      accessorKey: column.name,
      header: ({ table }) => {
        const { sortBy, sortOrder, onSort } = metaOf(table);
        const isSorted = sortBy === column.name;
        return (
          <UnstyledButton
            onClick={() => onSort(column.name)}
            title={column.name_translated}
            style={{ cursor: 'pointer', userSelect: 'none' }}
          >
            <Group gap={4} wrap="nowrap">
              <Text fz="xs" fw={500} c="purple.5" lineClamp={3}>
                {column.name_translated}
              </Text>
              {isSorted && (
                <IconChevronDown
                  size={12}
                  style={{
                    flexShrink: 0,
                    transform: sortOrder === 'asc' ? 'rotate(180deg)' : undefined,
                  }}
                />
              )}
            </Group>
          </UnstyledButton>
        );
      },
      cell: ({ row, table }) => resolveCellContent(column)(row.original, column, metaOf(table)),
    }),
  ),
  {
    id: EDIT_COLUMNS_COLUMN_ID,
    header: ({ table }) => {
      const { t, onEditColumns } = metaOf(table);
      return (
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={onEditColumns}
          title={t(i18nKeys.CHOOSE_COLUMNS)}
          aria-label={t(i18nKeys.CHOOSE_COLUMNS)}
        >
          <IconSettings size={16} />
        </ActionIcon>
      );
    },
    cell: () => null,
  },
];
